import { track } from '@cartwheel/analytics'
import { type CalendarAppointment, useCalendarAppointmentsQuery } from '@cartwheel/api/v1'
import { ErrorView, LoadingView } from '@cartwheel/components'
import { useEffect, useRef, useState } from 'react'
import { AppointmentDetailModal } from './appointment-detail-modal'
import { DropdownFilters, TimeFilter } from './calendar-filters'
import { MonthView } from './month-view'
import { useCalendarUrlState } from './use-calendar-url-state'
import { formatWeekRange, getWeekMonth, getWeekStart, MONTH_NAMES, toDateStr } from './utils'
import { WeekView } from './week-view'

interface AppointmentCalendarProps {
  organizationId?: string
  organizationName?: string
  userRoles?: string[]
  showSchoolDropdown?: boolean
}

export default function AppointmentCalendar({
  organizationId,
  organizationName,
  userRoles = [],
  showSchoolDropdown = true,
}: AppointmentCalendarProps) {
  const {
    now,
    viewMode,
    setViewMode,
    year,
    setYear,
    month,
    setMonth,
    weekStart,
    setWeekStart,
    selectedInstitutionIds,
    setSelectedInstitutionIds,
    startHour,
    setStartHour,
    endHour,
    setEndHour,
  } = useCalendarUrlState()

  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set())
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[] | null>(null)
  const [selectedClinicianNames, setSelectedClinicianNames] = useState<string[] | null>(null)
  const [selectedAppointment, setSelectedAppointment] = useState<CalendarAppointment | null>(null)

  const orgId = organizationId && organizationId !== 'all' ? organizationId : undefined

  const queryParams =
    viewMode === 'week'
      ? (() => {
          const weekEnd = new Date(weekStart)
          weekEnd.setDate(weekStart.getDate() + 6)
          return {
            start_date: toDateStr(weekStart),
            end_date: toDateStr(weekEnd),
            organization_id: orgId,
          }
        })()
      : { year, month, organization_id: orgId }

  const { data, isLoading, error } = useCalendarAppointmentsQuery({
    query: queryParams,
  })

  const allAppointments = data?.appointments ?? []

  const institutions = Array.from(
    new Map(
      allAppointments
        .filter((a) => a.institution_id && a.institution_name)
        .map((a) => [a.institution_id, { id: a.institution_id, name: a.institution_name }] as const)
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name))

  const students = Array.from(
    new Map(
      allAppointments.map((a) => [
        a.student_id,
        { id: a.student_id, name: `${a.student_first_name} ${a.student_last_name}` },
      ])
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name))

  const clinicians = Array.from(
    new Set(allAppointments.map((a) => a.clinician_name).filter(Boolean))
  ).sort() as string[]

  const filteredAppointments = allAppointments
    .filter((a) => {
      if (selectedInstitutionIds === null) return true // default: show all
      if (selectedInstitutionIds.length === 0) return false // explicitly cleared: show none
      return a.institution_id === null || selectedInstitutionIds.includes(a.institution_id)
    })
    .filter((a) => selectedStudentIds === null || selectedStudentIds.includes(a.student_id))
    .filter(
      (a) =>
        selectedClinicianNames === null ||
        (a.clinician_name !== null &&
          a.clinician_name !== undefined &&
          selectedClinicianNames.includes(a.clinician_name))
    )
    .filter((a) => {
      if (!a.start_time) return true
      const hour = new Date(a.start_time).getHours()
      return hour >= startHour && hour <= endHour
    })

  // Group appointments by date string (YYYY-MM-DD)
  const appointmentsByDate: Record<string, CalendarAppointment[]> = {}
  for (const appt of filteredAppointments) {
    if (appt.date) {
      if (!appointmentsByDate[appt.date]) {
        appointmentsByDate[appt.date] = []
      }
      appointmentsByDate[appt.date].push(appt)
    }
  }

  // --- RCM-921: CALENDAR_VIEWED ---
  const containerRef = useRef<HTMLDivElement>(null)
  const hasTrackedView = useRef(false)
  useEffect(() => {
    if (hasTrackedView.current || isLoading || !data) return
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true
          const noRoiAppointmentCount = allAppointments.filter((a) => !a.has_school_roi).length
          track('CALENDAR_VIEWED', {
            userRoles,
            organizationId: organizationId ?? null,
            organizationName: organizationName ?? null,
            month,
            year,
            viewMode,
            appointmentCount: filteredAppointments.length,
            noRoiAppointmentCount,
          })
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [
    isLoading,
    data,
    allAppointments,
    userRoles,
    organizationId,
    organizationName,
    month,
    year,
    viewMode,
    filteredAppointments.length,
  ])

  // --- RCM-921: CALENDAR_EMPTY_STATE_VIEWED ---
  useEffect(() => {
    if (isLoading || !data || filteredAppointments.length > 0) return

    track('CALENDAR_EMPTY_STATE_VIEWED', {
      userRoles,
      organizationId: organizationId ?? null,
      organizationName: organizationName ?? null,
      month,
      year,
      viewMode,
      hasSchoolFilterActive: selectedInstitutionIds !== null,
      startHour,
      endHour,
    })
  }, [
    userRoles,
    organizationId,
    organizationName,
    month,
    year,
    viewMode,
    isLoading,
    data,
    filteredAppointments.length,
    selectedInstitutionIds,
    startHour,
    endHour,
  ])

  function goToPrev() {
    const direction = 'prev'
    if (viewMode === 'month') {
      const newMonth = month === 1 ? 12 : month - 1
      const newYear = month === 1 ? year - 1 : year
      setMonth(newMonth)
      if (month === 1) setYear((y) => y - 1)
      setSelectedInstitutionIds(null)
      track('CALENDAR_MONTH_CHANGED', {
        userRoles,
        organizationId: organizationId ?? null,
        organizationName: organizationName ?? null,
        direction,
        viewMode,
        newMonth,
        newYear,
      })
    } else {
      const prev = new Date(weekStart)
      prev.setDate(prev.getDate() - 7)
      setWeekStart(prev)
      const { year: wy, month: wm } = getWeekMonth(prev)
      if (wy !== year || wm !== month) {
        setYear(wy)
        setMonth(wm)
        setSelectedInstitutionIds(null)
      }
      track('CALENDAR_MONTH_CHANGED', {
        userRoles,
        organizationId: organizationId ?? null,
        organizationName: organizationName ?? null,
        direction,
        viewMode,
        newMonth: wm,
        newYear: wy,
      })
    }
  }

  function goToNext() {
    const direction = 'next'
    if (viewMode === 'month') {
      const newMonth = month === 12 ? 1 : month + 1
      const newYear = month === 12 ? year + 1 : year
      setMonth(newMonth)
      if (month === 12) setYear((y) => y + 1)
      setSelectedInstitutionIds(null)
      track('CALENDAR_MONTH_CHANGED', {
        userRoles,
        organizationId: organizationId ?? null,
        organizationName: organizationName ?? null,
        direction,
        viewMode,
        newMonth,
        newYear,
      })
    } else {
      const next = new Date(weekStart)
      next.setDate(next.getDate() + 7)
      setWeekStart(next)
      const { year: wy, month: wm } = getWeekMonth(next)
      if (wy !== year || wm !== month) {
        setYear(wy)
        setMonth(wm)
        setSelectedInstitutionIds(null)
      }
      track('CALENDAR_MONTH_CHANGED', {
        userRoles,
        organizationId: organizationId ?? null,
        organizationName: organizationName ?? null,
        direction,
        viewMode,
        newMonth: wm,
        newYear: wy,
      })
    }
  }

  function handleSetViewMode(mode: 'month' | 'week') {
    track('CALENDAR_VIEW_MODE_CHANGED', {
      userRoles,
      organizationId: organizationId ?? null,
      organizationName: organizationName ?? null,
      from: viewMode,
      to: mode,
    })
    if (mode === 'week') {
      const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1
      const anchor = isCurrentMonth ? now : new Date(year, month - 1, 1)
      setWeekStart(getWeekStart(anchor))
    } else {
      const { year: wy, month: wm } = getWeekMonth(weekStart)
      setYear(wy)
      setMonth(wm)
    }
    setViewMode(mode)
  }

  function handleInstitutionChange(ids: string[]) {
    const action =
      ids.length === institutions.length ? 'select_all' : ids.length === 0 ? 'clear' : 'toggle'
    track('CALENDAR_SCHOOL_FILTER_CHANGED', {
      userRoles,
      organizationId: organizationId ?? null,
      organizationName: organizationName ?? null,
      selectedCount: ids.length,
      totalCount: institutions.length,
      action,
    })
    setSelectedInstitutionIds(ids)
  }

  function handleStartHourChange(hour: number) {
    track('CALENDAR_TIME_FILTER_CHANGED', {
      userRoles,
      organizationId: organizationId ?? null,
      organizationName: organizationName ?? null,
      field: 'start',
      newHour: hour,
    })
    setStartHour(hour)
  }

  function handleEndHourChange(hour: number) {
    track('CALENDAR_TIME_FILTER_CHANGED', {
      userRoles,
      organizationId: organizationId ?? null,
      organizationName: organizationName ?? null,
      field: 'end',
      newHour: hour,
    })
    setEndHour(hour)
  }

  function handleAppointmentClick(appointment: CalendarAppointment) {
    track('CALENDAR_APPOINTMENT_OPENED', {
      userRoles,
      organizationId: organizationId ?? null,
      organizationName: organizationName ?? null,
      appointmentId: appointment.id,
      hasSchoolRoi: appointment.has_school_roi,
      hasVideoLink: null,
      status: appointment.status ?? null,
      viewMode,
    })
    setSelectedAppointment(appointment)
  }

  if (isLoading) {
    return <LoadingView title="Loading appointments..." />
  }

  if (error) {
    return (
      <ErrorView
        title="Error loading appointments"
        message="Unable to load appointment calendar."
      />
    )
  }

  return (
    <div ref={containerRef} className="rounded-sm border border-gray-stroke bg-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div />
        {/* Title rendered by parent Rails view */}
        <div className="flex items-center gap-3">
          <TimeFilter
            startHour={startHour}
            endHour={endHour}
            onStartHourChange={handleStartHourChange}
            onEndHourChange={handleEndHourChange}
          />
          {/* Month / Week toggle */}
          <div className="flex items-center border border-gray-stroke rounded overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => handleSetViewMode('month')}
              className={`px-3 py-1 ${
                viewMode === 'month'
                  ? 'bg-forest-dark text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Month
            </button>
            <button
              type="button"
              onClick={() => handleSetViewMode('week')}
              className={`px-3 py-1 border-l border-gray-stroke ${
                viewMode === 'week'
                  ? 'bg-forest-dark text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Week
            </button>
          </div>
        </div>
      </div>

      {/* Dropdown filters */}
      <DropdownFilters
        showSchoolDropdown={showSchoolDropdown}
        institutions={institutions}
        students={students}
        clinicians={clinicians}
        month={month}
        year={year}
        onInstitutionChange={handleInstitutionChange}
        onStudentChange={setSelectedStudentIds}
        onClinicianChange={setSelectedClinicianNames}
      />

      {/* Navigation */}
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goToPrev}
          className="text-gray-500 hover:text-gray-800 text-lg px-1"
          aria-label={viewMode === 'month' ? 'Previous month' : 'Previous week'}
        >
          ‹
        </button>
        <h3 className="text-xl font-bold text-gray-900">
          {viewMode === 'month' ? `${MONTH_NAMES[month - 1]} ${year}` : formatWeekRange(weekStart)}
        </h3>
        <button
          type="button"
          onClick={goToNext}
          className="text-gray-500 hover:text-gray-800 text-lg px-1"
          aria-label={viewMode === 'month' ? 'Next month' : 'Next week'}
        >
          ›
        </button>
      </div>

      {/* Calendar */}
      {viewMode === 'month' ? (
        <MonthView
          year={year}
          month={month}
          now={now}
          appointmentsByDate={appointmentsByDate}
          expandedDays={expandedDays}
          onExpandDay={(dateStr) => setExpandedDays((prev) => new Set([...prev, dateStr]))}
          onAppointmentClick={handleAppointmentClick}
        />
      ) : (
        <WeekView
          weekStart={weekStart}
          appointmentsByDate={appointmentsByDate}
          now={now}
          onAppointmentClick={handleAppointmentClick}
        />
      )}

      {/* Empty state */}
      {filteredAppointments.length === 0 && (
        <p className="text-center text-sm text-gray-400 mt-4">
          {viewMode === 'month'
            ? `No appointments scheduled for ${MONTH_NAMES[month - 1]} ${year}.`
            : `No appointments scheduled for the week of ${formatWeekRange(weekStart)}.`}
        </p>
      )}

      {/* Detail modal */}
      {selectedAppointment && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
          trackingProps={{
            userRoles,
            organizationId: organizationId ?? null,
            organizationName: organizationName ?? null,
          }}
        />
      )}
    </div>
  )
}
